import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { UI } from '../pages/legalContent';

const Footer = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const getLocalizedPath = (path) => `/${language}${path}`;

  const footerLinks = [
    { path: '/platform', label: t('nav.platform') },
    { path: '/industries', label: t('nav.industries') },
    { path: '/exposure', label: t('nav.operationalLoss') || 'Operational Loss' },
    { path: '/implementation', label: t('nav.exploreImplementation') || 'Explore Implementation' },
    { path: '/operational-risk-data', label: t('nav.operationalRiskResearch') || 'Operational Risk Research' },
    { path: '/contact', label: t('nav.contact') },
  ];

  return (
    <footer className="bg-slate-900 text-white" data-testid="main-footer">
      <div className="container-max section-padding py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to={getLocalizedPath('/')} className="inline-block mb-2">
              <img 
                src="/tapelligence_logo_transparent.png" 
                alt="Tapelligence" 
                className="h-[74px] w-auto"
              />
            </Link>
            <p className="text-slate-500 text-sm font-medium tracking-wide mb-4">
              {t('footer.tagline')}
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">
              Navigation
            </h4>
            <ul className="space-y-3">
              {footerLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={getLocalizedPath(link.path)}
                    className="text-slate-300 hover:text-white transition-colors text-sm"
                    data-testid={`footer-link-${link.path.slice(1)}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to={`/${language}/security`}
                  className="text-slate-300 hover:text-white transition-colors text-sm"
                  data-testid="footer-link-security"
                >
                  {t('security.title')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Portal Access */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">
              Portal
            </h4>
            <a
              href="https://portal.tapelligence.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-slate-100 rounded-md px-5 py-2.5 text-sm font-medium transition-all"
              data-testid="footer-portal-btn"
            >
              {t('nav.portalLogin')}
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              &copy; {new Date().getFullYear()} {t('footer.copyright')}
            </p>
            <div className="flex items-center gap-6 text-sm">
              <Link to={getLocalizedPath('/terms')} className="text-slate-400 hover:text-white transition-colors" data-testid="footer-link-terms">{UI[language]?.terms || UI.en.terms}</Link>
              <Link to={getLocalizedPath('/privacy')} className="text-slate-400 hover:text-white transition-colors" data-testid="footer-link-privacy">{UI[language]?.privacy || UI.en.privacy}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
